import React, { useEffect, useState } from "react";

import { format } from "date-fns";

import { API, graphqlOperation, Storage } from "aws-amplify";
import { getReceiptCounter } from "../../../graphql/reloadyard/custom-queries";
import { updateReloadYard } from "../../../graphql/mutations";
import { performUpdateInboundMini } from "../../../graphql/inbound/inboundCRUD";

import {
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { Button, TopToolbar, useNotify } from "react-admin";

import {
  CancelIcon,
  CloseIcon,
  DocumentIcon,
  ExceptionIcon,
} from "../../../components/common/iconography";

import { BackButton } from "../../../components/common/BackButton";
import {
  getCustomFieldIdCleansed,
  parseCustomField,
} from "../../../components/common/CustomFields";
import { HelpButton } from "../../../components/common/HelpButton";
import { SendPOSDialogButton } from "../../../components/common/SendPOSDialogButton";

import { isAuthorized, logError } from "../../../utils/appUtils";
import {
  AclType,
  FilePaths,
  InboundStatus,
  Unit,
} from "../../../utils/constants";
import {
  downloadFile,
  fetchTemplatePath,
  getTemplatePath,
} from "../../../utils/fileUtils";
import {
  calculateMbf,
  calculateMsf,
  convertUnits,
  getPPU,
  getUnitName,
  isLumberCommodity,
  isPanelsCommodity,
} from "../../../utils/loadTypeUtils";

import { InboundEditHelp } from "./InboundHelp";
import { CreateInvoiceButton } from "./CreateInvoiceButton";
import * as utils from "./InboundUtils";
import { getBarcodesString, trimDecimal } from "../../../utils/utils";
import { fetchLatestGateCheckIn } from "../gateCheckIns/gateCheckInUtils";

// TEMPORARY — ATS only (American Transloading Solutions). Their receipt
// template prints a "Driver's Name" custom field; until the driver name has a
// proper home on the document, getReceiptData fills that field from the
// shipment's gate check-in row. Remove once the permanent field lands.
const ATS_RELOAD_YARD_ID = "18567be7-5e38-4a33-b947-f380620326c1";
const fetchAtsGateCheckIn = async (shipment) => {
  if (shipment?.reloadYardId !== ATS_RELOAD_YARD_ID) return null;
  try {
    return await fetchLatestGateCheckIn("in", shipment.id);
  } catch (err) {
    // The driver name is a nice-to-have; never block the receipt on it.
    logError(
      "inboundShipment:InboundEditActions:fetchAtsGateCheckIn",
      err,
      false,
    );
    return null;
  }
};

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  dialogTitle: {
    display: "flex",
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(0),
    justifyContent: "space-between",
  },
  title: {
    paddingTop: theme.spacing(1),
  },
  dialogContent: {
    // https://stackoverflow.com/questions/47698037/how-can-i-set-a-height-to-a-dialog-in-material-ui
    minHeight: "50vh",
  },
}));

const InboundEditActions = ({
  data,
  loading,
  setReceiptFile,
  setReceiptNumber,
  exceptions,
  setExceptions,
  allSealsValid,
  ...props
}) => {
  const classes = useStyles();
  const isMedium = useMediaQuery((theme) => theme.breakpoints.up("sm"));
  const notify = useNotify();
  const [isSaving, setIsSaving] = useState(false);
  const [isExceptionDialogOpen, setIsExceptionDialogOpen] = useState();

  const getProductData = (product, quantityReceived, totalUOM) => {
    if (product) {
      const productData = {
        name: product.name,
        productCode: product.productCode,
        category: product.category,
        defaultUOM: product.defaultUOM,
        defaultWeightUOM: product.defaultWeightUOM,
        width: product.width,
        length: product.length,
        height: product.height,
      };
      if (quantityReceived && totalUOM) {
        Object.keys(Unit).forEach((key) => {
          if (key !== totalUOM) {
            const unitQty = convertUnits(
              quantityReceived,
              totalUOM,
              key,
              product,
            );
            if (unitQty?.value) productData[key] = unitQty.value;
          } else {
            productData[key] = quantityReceived;
          }
        });
      }

      return productData;
    } else return undefined;
  };

  const getReceiptData = (data, receiptNumber, gateCheckIn = null) => {
    const receiptData = { ...data };
    const shipDate = receiptData.releaseDateTime
      ? new Date(receiptData.releaseDateTime).toLocaleDateString()
      : receiptData.endDateTime
        ? new Date(receiptData.endDateTime).toLocaleDateString()
        : receiptData.placedDateTime
          ? new Date(receiptData.placedDateTime).toLocaleDateString()
          : new Date(receiptData.createDateTime).toLocaleDateString();
    const grossWeight =
      !isNaN(receiptData.ladenWeight) && !isNaN(receiptData.tareWeight)
        ? Number(receiptData.ladenWeight) - Number(receiptData.tareWeight)
        : "";

    const docData = {
      accountName: receiptData.accountName,
      account: {
        name: receiptData.accountName,
        street: receiptData.account?.address?.street,
        city: receiptData.account?.address?.city,
        state: receiptData.account?.address?.state,
        zipCode: receiptData.account?.address?.zipCode,
        country: receiptData.account?.address?.country,
        phone: receiptData.account?.phone,
        email: receiptData.account?.email,
      },
      arrivalDate: receiptData.placedDateTime
        ? format(new Date(receiptData.placedDateTime), "MM/dd/yyyy")
        : undefined,
      arrivalTime: receiptData.placedDateTime
        ? format(new Date(receiptData.placedDateTime), "hh:mm a")
        : undefined,
      receiptNumber: receiptNumber,
      carrierName: receiptData.carrierName,
      endDate: receiptData.endDateTime
        ? format(new Date(receiptData.endDateTime), "MM/dd/yyyy")
        : undefined,
      endTime: receiptData.endDateTime
        ? format(new Date(receiptData.endDateTime), "hh:mm a")
        : undefined,
      grossWeight: grossWeight,
      ladenWeight: receiptData.ladenWeight,
      netWeight: receiptData.netWeight,
      poNumber: receiptData.poNumber,
      railCarNumber: receiptData.railCarNumber
        ? receiptData.railCarNumber
        : null,
      reloadYard: {
        name: window.appUser.reloadYard.name,
        street: window.appUser.reloadYard.street,
        city: window.appUser.reloadYard.city,
        state: window.appUser.reloadYard.state,
        zipCode: window.appUser.reloadYard.zipCode,
        phone: window.appUser.reloadYard.phone,
        email: window.appUser.reloadYard.email,
        website: window.appUser.reloadYard.website,
      },
      releaseDate: receiptData.releaseDateTime
        ? format(new Date(receiptData.releaseDateTime), "MM/dd/yyyy")
        : undefined,
      releaseTime: receiptData.releaseDateTime
        ? format(new Date(receiptData.releaseDateTime), "hh:mm a")
        : undefined,
      scaleWeightUom: receiptData.scaleWeightUnits,
      scaleWeightUomName: getUnitName(receiptData.scaleWeightUnits),
      shipDate: shipDate,
      shipFrom: receiptData.shipFrom,
      startDate: receiptData.startDateTime
        ? format(new Date(receiptData.startDateTime), "MM/dd/yyyy")
        : undefined,
      startTime: receiptData.startDateTime
        ? format(new Date(receiptData.startDateTime), "hh:mm a")
        : undefined,
      tareWeight: receiptData.tareWeight,
      totalQuantity: utils.calcTotalQuantity(receiptData),
      uom: receiptData.totalUOM,
      uomName: getUnitName(receiptData.totalUOM),
      weightUom: receiptData.weightUnits,
      weightUomName: getUnitName(receiptData.weightUnits),
    };

    if (receiptData.seals) {
      docData.sealNumbers = getBarcodesString(receiptData.seals?.items);
    }

    // add inbound shipment custom fields
    if (receiptData.customFields?.length > 0) {
      const customHeaderMap = new Map();
      receiptData.customFields.forEach((field) => {
        const fieldValues = parseCustomField(field);

        if (customHeaderMap.has(fieldValues.name)) {
          docData.customFields[customHeaderMap.get(fieldValues.name)] =
            fieldValues.value;
        } else {
          const columnId = getCustomFieldIdCleansed(fieldValues.name);
          customHeaderMap.set(fieldValues.name, columnId);
          docData.customFields = {
            ...docData.customFields,
            [columnId]: fieldValues.value,
          };
        }
      });
    }

    // TEMPORARY — ATS only: the gate check-in's driver name overrides the
    // "Driver's Name" custom field (cleansed id "driversName"). See
    // fetchAtsGateCheckIn above.
    if (
      receiptData.reloadYardId === ATS_RELOAD_YARD_ID &&
      gateCheckIn?.driverName
    ) {
      docData.customFields = {
        ...docData.customFields,
        driversName: gateCheckIn.driverName,
      };
    }

    // add product and inventory custom fields to each line item
    if (receiptData.inventories?.items?.length > 0) {
      docData.lineItems = [];

      // Initialize totals and flags
      let allMissingPCS = true;
      let somePCSNull = false;
      let totalPCS = 0;

      let totalMBF = 0;
      let allMissingMBF = true;
      let someMBFNull = false;

      let totalMSF = 0;
      let allMissingMSF = true;
      let someMSFNull = false;

      receiptData.inventories.items.forEach((lineItem, index) => {
        const mergedPcs = getPPU(lineItem);

        docData.lineItems[index] = {
          productCode: lineItem.productCode,
          productName: lineItem.productName,
          lotNumber: lineItem.lotNumber,
          railCarNumber: lineItem.railCarNumber || receiptData.railCarNumber,
          quantityReceived:
            lineItem.quantityReceived || lineItem.quantityAdvised,
          uom: lineItem.inventoryUOM
            ? lineItem.inventoryUOM
            : receiptData.totalUOM,
          uomName: getUnitName(
            lineItem.inventoryUOM
              ? lineItem.inventoryUOM
              : receiptData.totalUOM,
          ),
          weight: lineItem.weight,
          weightUom: lineItem.weightUOM,
          weightUomName: getUnitName(lineItem.weightUOM),
        };
        if (lineItem.product) {
          docData.lineItems[index].product = getProductData(
            lineItem.product,
            lineItem.quantityReceived || lineItem.quantityAdvised,
            lineItem.inventoryUOM,
          );
        }

        const ppu = mergedPcs;
        docData.lineItems[index].ppu = ppu;

        // Update PCS totals and flags
        if (ppu == null || ppu === "") {
          somePCSNull = true;
        } else {
          allMissingPCS = false;

          if (receiptData.totalUOM === Unit.UNIT) {
            const pcs = trimDecimal(ppu * lineItem.quantityReceived);
            docData.lineItems[index].pcs = pcs;
            totalPCS = trimDecimal(totalPCS + pcs);
          } else {
            docData.lineItems[index].pcs = ppu;
            totalPCS = trimDecimal(totalPCS + ppu);
          }
        }

        const loadType =
          lineItem.reloadYardCommodityId || receiptData.reloadYardCommodityId;
        if (isLumberCommodity(loadType)) {
          const mbf = calculateMbf(lineItem);
          if (mbf == null) {
            someMBFNull = true;
          } else {
            allMissingMBF = false;
            docData.lineItems[index].mbf = mbf;
            totalMBF = trimDecimal(totalMBF + mbf);
          }
        } else if (isPanelsCommodity(loadType)) {
          const msf = calculateMsf(lineItem);
          if (msf == null) {
            someMSFNull = true;
          } else {
            allMissingMSF = false;
            docData.lineItems[index].msf = msf;
            totalMSF = trimDecimal(totalMSF + msf, 3);
          }
        }

        if (lineItem.customFields?.length > 0) {
          const customHeaderMap = new Map();
          lineItem.customFields.forEach((field) => {
            const fieldValues = parseCustomField(field);

            if (customHeaderMap.has(fieldValues.name)) {
              docData.lineItems[index].customFields[
                customHeaderMap.get(fieldValues.name)
              ] = fieldValues.value;
            } else {
              const columnId = getCustomFieldIdCleansed(fieldValues.name);
              customHeaderMap.set(fieldValues.name, columnId);
              docData.lineItems[index].customFields = {
                ...docData.lineItems[index].customFields,
                [columnId]: fieldValues.value,
              };
            }
          });
        }
      });

      // Finalize totalPCS
      if (!allMissingPCS) {
        if (somePCSNull) {
          docData.totalPCS = "N/A";
        } else {
          docData.totalPCS = totalPCS;
        }
      }

      // Finalize totalMBF
      if (!allMissingMBF) {
        if (someMBFNull) {
          docData.totalMBF = "N/A";
        } else {
          docData.totalMBF = totalMBF;
        }
      }

      // Finalize totalMSF
      if (!allMissingMSF) {
        if (someMSFNull) {
          docData.totalMSF = "N/A";
        } else {
          docData.totalMSF = totalMSF;
        }
      }
    }

    debugger;
    return docData;
  };

  const generateReceipt = async () => {
    setIsSaving(true);
    debugger;

    try {
      let receiptNumber = data.receiptNumber;

      if (!receiptNumber) {
        const result = await API.graphql(
          graphqlOperation(getReceiptCounter, {
            id: window.appUser?.reloadYardId,
          }),
        );
        const counter = ++result.data.getReloadYard.receiptCounter;
        await API.graphql(
          graphqlOperation(updateReloadYard, {
            input: {
              id: window.appUser?.reloadYardId,
              active: result.data.getReloadYard.active,
              name: result.data.getReloadYard.name,
              receiptCounter: counter,
            },
          }),
        );

        receiptNumber = `IR-${counter}`;
        setReceiptNumber(receiptNumber);
      }

      const gateCheckIn = await fetchAtsGateCheckIn(data); // TEMPORARY — ATS only
      const docData = getReceiptData(data, receiptNumber, gateCheckIn);
      docData.logo = window.appUser.reloadYard.logoImage
        ? {
            _type: "image",
            source: window.appUser.reloadYard.logoImage.key,
            format: "image/png",
            width: 70,
            height: 70,
          }
        : undefined;
      docData.transloadsLogo = {
        _type: "image",
        source: `${FilePaths.TEMPLATES}/${FilePaths.TRANSLOADS_LOGO}`,
        format: "image/png",
        width: 100,
        height: 25,
      };

      const outputPath = `${data.reloadYardId}/${FilePaths.INBOUND}/${
        data.id
      }/${receiptNumber}_${new Date()
        .toLocaleDateString()
        .replaceAll("/", "_")}.docx`;

      const createDocx = async (templateKey) =>
        API.post("transREST", "/createDocx", {
          body: {
            templateKey,
            data: docData,
            outputFileKey: outputPath,
          },
        });

      const initialTemplatePath = getTemplatePath(
        data.account,
        "receiptTemplate",
      );
      let response = await createDocx(initialTemplatePath);

      // If the cached key is stale (template replaced), re-fetch the latest template key and retry once.
      if (!response?.body?.key) {
        const freshTemplatePath = await fetchTemplatePath(
          data.account,
          "receiptTemplate",
        );
        response = await createDocx(freshTemplatePath);
      }

      if (response.body?.key) {
        const file = await Storage.get(response.body.key, { download: true });
        downloadFile(file, response.body.key);
        setReceiptFile(response.body);
        let toUpdateRecord = {};

        try {
          // must call lambda to avoid setting updatedAt timestamp
          await performUpdateInboundMini({
            ...data,
            receiptFile: response.body,
            receiptNumber: receiptNumber,
          });
        } catch (error) {
          notify("Receitp created with errors.", "warning");
          logError(
            "InboundEdit:EditActions:generateReceipt:updateInbound",
            { error: error, input: toUpdateRecord },
            false,
          );
          setIsSaving(false);
          return;
        }

        if (response.success) notify("Receipt created successfully.");
        else if (response.warnings) {
          notify("Receipt created with errors.", "warning");
          logError(
            "InboundEdit:EditActions:generateReceipt:warning",
            response.warnings,
            false,
            null,
          );
        }
      } else {
        logError(
          "InboundEdit:EditActions:generateReceipt:error",
          response.error,
          notify,
          null,
        );
        setIsSaving(false);
        return;
      }
    } catch (error) {
      logError("InboundEdit:EditActions:generateReceipt", error, notify, null);
    }
    setIsSaving(false);
  };

  return (
    <TopToolbar>
      <Backdrop className={classes.backdrop} open={isSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <HelpButton>
        <InboundEditHelp
          {...props}
          receiptData={getReceiptData(data, "RI-number")}
        />
      </HelpButton>
      {(data?.status === InboundStatus.PLACED ||
        data?.status === InboundStatus.SIT_PLACED) && (
        <Button
          label="Exception"
          color="primary"
          disabled={isSaving}
          onClick={() => setIsExceptionDialogOpen(true)}
        >
          <ExceptionIcon />
        </Button>
      )}
      {isMedium && isAuthorized(AclType.INVOICES) && (
        <CreateInvoiceButton {...props} record={data} disabled={loading} />
      )}
      {data && !data.status?.startsWith(InboundStatus.ADVICE_ALL) && (
        <Button
          label="Receipt"
          color="primary"
          disabled={isSaving || loading}
          onClick={generateReceipt}
        >
          <DocumentIcon />
        </Button>
      )}
      {data && !data.status?.startsWith(InboundStatus.ADVICE_ALL) && (
        <SendPOSDialogButton
          label="SEND"
          title="Send POD"
          record={data}
          email={data.account?.podEmail}
          subjectDefault={utils.getPODEmailSubject(data)}
          enclosePhotosDefault={data.account?.podPhotos}
          encloseDocsDefault={data.account?.podAttachments}
          handleSend={utils.handleSendPOD}
          disabled={loading}
        />
      )}
      <BackButton {...props} />
      <ExceptionDialog
        isExceptionDialogOpen={isExceptionDialogOpen}
        setIsExceptionDialogOpen={setIsExceptionDialogOpen}
        exceptions={exceptions}
        setExceptions={setExceptions}
        classes={classes}
        allSealsValid={allSealsValid}
      />
    </TopToolbar>
  );
};

const ExceptionDialog = ({
  isExceptionDialogOpen,
  setIsExceptionDialogOpen,
  exceptions,
  setExceptions,
  classes,
  allSealsValid,
}) => {
  const [otherExceptionNoteError, setOtherExceptionError] = useState();
  const [tempExceptions, setTempExceptinos] = useState({});
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));

  useEffect(() => {
    if (isExceptionDialogOpen) {
      setTempExceptinos(exceptions);
    }
  }, [isExceptionDialogOpen]);

  const handleSave = async () => {
    if (!validate()) return;
    debugger;
    tempExceptions.hasException =
      tempExceptions.unsafeShipment ||
      tempExceptions.brokenSeal ||
      tempExceptions.otherException;
    setExceptions({ ...tempExceptions });
    setIsExceptionDialogOpen(false);
  };

  const validate = () => {
    let isValid = true;
    setOtherExceptionError("");
    if (tempExceptions.otherException) {
      isValid = isValid && tempExceptions.otherExceptionNote;
      !tempExceptions.otherExceptionNote && setOtherExceptionError("Required");
    }
    return isValid;
  };

  const handleChange = (evt) => {
    const type = evt.target.name;
    const value = evt.target.checked;
    debugger;
    if (type === "unsafeShipment") {
      tempExceptions.unsafeShipment = value;
      if (!value) {
        tempExceptions.unsafeShipmentNote = "";
      }
    } else if (type === "brokenSeal") {
      tempExceptions.brokenSeal = value;
      if (!value) {
        tempExceptions.brokenSealNote = "";
      }
    } else {
      tempExceptions.otherException = value;
      if (!value) {
        tempExceptions.otherExceptionNote = "";
        setOtherExceptionError("");
      }
    }
    setTempExceptinos({ ...tempExceptions });
  };

  const handleCancel = () => {
    setIsExceptionDialogOpen(false);
  };

  const setNote = (prop, value) => {
    tempExceptions[prop] = value;
    setTempExceptinos({ ...tempExceptions });

    if (prop === "otherExceptionNote" && value) setOtherExceptionError("");
  };

  return (
    <Dialog
      open={isExceptionDialogOpen}
      maxWidth={isSmall ? "xl" : "md"}
      fullWidth={true}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography className={classes.title} variant="h6">
          Exception Manager
        </Typography>
        <IconButton aria-label="close" onClick={handleCancel}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <FormControl component="fieldset" className={classes.formControl}>
          <FormLabel>
            <Typography variant="overline" color="textSecondary">
              Exception Type
            </Typography>
          </FormLabel>
          <FormGroup row={!isSmall}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={tempExceptions.unsafeShipment}
                  onChange={handleChange}
                  name="unsafeShipment"
                />
              }
              label="Unsafe Shipment"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={tempExceptions.brokenSeal}
                  onChange={handleChange}
                  disabled={tempExceptions.brokenSeal && !allSealsValid}
                  name="brokenSeal"
                />
              }
              label="Broken seal"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={tempExceptions.otherException}
                  onChange={handleChange}
                  name="other"
                />
              }
              label="Other"
            />
          </FormGroup>
        </FormControl>
        {tempExceptions.unsafeShipment && (
          <div className={classes.exceptionContainer}>
            <TextField
              name="unsafeShipmentNote"
              label="Unsafe Shipment Notes"
              multiline
              autoFocus
              minRows={3}
              margin="dense"
              variant="outlined"
              fullWidth
              value={
                tempExceptions.unsafeShipmentNote
                  ? tempExceptions.unsafeShipmentNote
                  : ""
              }
              onChange={(evt) =>
                setNote("unsafeShipmentNote", evt.target.value)
              }
            />
          </div>
        )}
        {tempExceptions.brokenSeal && (
          <div className={classes.exceptionContainer}>
            <TextField
              name="brokenSealNote"
              label="Broken Seal Notes"
              multiline
              autoFocus
              minRows={3}
              margin="dense"
              variant="outlined"
              fullWidth
              value={
                tempExceptions.brokenSealNote
                  ? tempExceptions.brokenSealNote
                  : ""
              }
              onChange={(evt) => setNote("brokenSealNote", evt.target.value)}
            />
          </div>
        )}
        {tempExceptions.otherException && (
          <div className={classes.exceptionContainer}>
            <TextField
              name="otherExceptionNote"
              label="Other Notes"
              multiline
              autoFocus
              minRows={3}
              error={otherExceptionNoteError}
              helperText={otherExceptionNoteError}
              margin="dense"
              variant="outlined"
              required
              fullWidth
              value={
                tempExceptions.otherExceptionNote
                  ? tempExceptions.otherExceptionNote
                  : ""
              }
              onChange={(evt) =>
                setNote("otherExceptionNote", evt.target.value)
              }
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Box>
          <Box>
            <Button
              label="Cancel"
              color="default"
              onClick={() => handleCancel()}
            >
              <CancelIcon />
            </Button>
            <Button
              label="Submit"
              onClick={() => handleSave()}
              color="primary"
            />
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default InboundEditActions;
